import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';

interface RelatedSearchesProps {
  config: {
    showHeading?: boolean;
    headingText?: string;
    headingStyle?: 'simple' | 'with-icon' | 'minimal';
    pillStyle?: 'filled' | 'outlined' | 'ghost';
    pillSize?: 'small' | 'medium' | 'large';
    pillColor?: string;
    textColor?: string;
    hoverStyle?: 'darken' | 'lighten' | 'invert';
    showSearchIcon?: boolean;
    layout?: 'wrap' | 'scroll' | 'grid';
    columns?: number;
    maxPills?: number;
    borderRadius?: 'square' | 'rounded' | 'pill';
    backgroundColor?: string;
    containerPadding?: 'none' | 'sm' | 'md' | 'lg' | 'custom';
    customPadding?: string;
    customCssClass?: string;
  };
  links: Array<{
    title: string;
    url: string;
  }>;
  compact?: boolean;
}

const useStyles = makeStyles((theme) => ({
  container: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '16px 20px',
    borderRadius: 8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  headingIcon: {
    width: 18,
    height: 18,
    color: '#666',
  },
  pillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  pillsScroll: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 8,
    '&::-webkit-scrollbar': {
      height: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#ccc',
      borderRadius: 4,
    },
  },
  pillsGrid: {
    display: 'grid',
    gap: 8,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    textDecoration: 'none',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  pillIcon: {
    flexShrink: 0,
  },
}));

export const RelatedSearches: React.FC<RelatedSearchesProps> = ({ config, links, compact }) => {
  const classes = useStyles();

  const {
    showHeading = true,
    headingText = 'Related Searches',
    headingStyle = 'with-icon',
    pillStyle = 'outlined',
    pillSize = 'medium',
    pillColor = '#2563eb',
    textColor = '#ffffff',
    showSearchIcon = false,
    layout = 'wrap',
    columns = 3,
    maxPills = 10,
    borderRadius = 'pill',
    backgroundColor = '#f8fafc',
    containerPadding = 'md',
    customPadding = '',
  } = config;

  // Size mappings
  const sizeMap = {
    small: { padding: '6px 12px', fontSize: 12, iconSize: 12 },
    medium: { padding: '8px 16px', fontSize: 13, iconSize: 14 },
    large: { padding: '12px 20px', fontSize: 15, iconSize: 16 },
  };

  const radiusMap = {
    square: 4,
    rounded: 8,
    pill: 50,
  };

  const size = sizeMap[pillSize] || sizeMap.medium;
  const radius = radiusMap[borderRadius] || radiusMap.pill;

  // Pill styles based on pillStyle
  const getPillStyles = () => {
    const base = {
      padding: size.padding,
      fontSize: size.fontSize,
      borderRadius: radius,
    };

    switch (pillStyle) {
      case 'filled':
        return {
          ...base,
          backgroundColor: pillColor,
          color: textColor,
          border: 'none',
        };
      case 'ghost':
        return {
          ...base,
          backgroundColor: 'transparent',
          color: pillColor,
          border: 'none',
        };
      case 'outlined':
      default:
        return {
          ...base,
          backgroundColor: 'transparent',
          color: pillColor,
          border: `1.5px solid ${pillColor}`,
        };
    }
  };

  // Container styles based on layout
  const getContainerClass = () => {
    switch (layout) {
      case 'scroll':
        return classes.pillsScroll;
      case 'grid':
        return classes.pillsGrid;
      default:
        return classes.pillsContainer;
    }
  };

  const getContainerStyles = () => {
    if (layout === 'grid') {
      return { gridTemplateColumns: `repeat(${columns}, 1fr)` };
    }
    return {};
  };

  // Heading styles
  const getHeadingStyles = () => {
    switch (headingStyle) {
      case 'minimal':
        return {
          margin: 0,
          fontSize: 13,
          fontWeight: 500,
          color: '#666',
          textTransform: 'uppercase' as const,
          letterSpacing: 0.5,
        };
      case 'simple':
      case 'with-icon':
      default:
        return {
          margin: 0,
          fontSize: 15,
          fontWeight: 600,
          color: '#1a1a2e',
        };
    }
  };

  const displayLinks = links.slice(0, maxPills);

  return (
    <Box
      className={classes.container}
      style={{
        backgroundColor,
        padding:
          containerPadding === 'custom' && customPadding
            ? customPadding
            : containerPadding === 'none'
            ? '0'
            : containerPadding === 'sm'
            ? '0.5rem'
            : containerPadding === 'lg'
            ? '1.5rem'
            : '1rem',
      }}
    >
      {showHeading && (
        <Box className={classes.header}>
          {headingStyle === 'with-icon' && <SearchIcon className={classes.headingIcon} />}
          <Typography component="h4" style={getHeadingStyles()}>
            {headingText}
          </Typography>
        </Box>
      )}

      <Box className={getContainerClass()} style={getContainerStyles()}>
        {displayLinks.map((link, index) => (
          <a
            key={index}
            href={link.url}
            className={classes.pill}
            style={{
              ...getPillStyles(),
              ...(layout === 'grid' ? { justifyContent: 'center', textAlign: 'center' } : {}),
            }}
            onClick={(e) => e.preventDefault()}
          >
            {showSearchIcon && (
              <SearchIcon className={classes.pillIcon} style={{ width: size.iconSize, height: size.iconSize }} />
            )}
            <span>{link.title}</span>
          </a>
        ))}
      </Box>
    </Box>
  );
};

export default RelatedSearches;
